import queryString from "query-string";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { getSettings } from "src/services/configService";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch } from "src/store";
import { useAnalyticsClick } from "src/ui/hooks/useAnalyticsClick";

import { DropdownItem } from "@dashboard/devices/components/shared";
import { BatchActionState, setBatchMode } from "@dashboard/devices/store";
import CreateGroupDialog from "@dashboard/groups/components/CreateGroupDialog";
import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { useTheme } from "@mui/material";

import DropdownContainer from "../shared/Dropdown/DropdownContainer";
import { AddDeviceModal } from "../Table/AddDeviceModal/AddDeviceModal";

import ExportCSV from "./ExportCSV";

type SelectBatchActionMode = (entity: BatchActionState["entity"], type: BatchActionState["type"]) => void;

type Props = {
    anchorRef: React.RefObject<HTMLButtonElement | null>;
    onSelectColumns: () => void;
};

export const Menu = ({ anchorRef, onSelectColumns }: Props) => {
    const [isCreateGroupDialogOpen, setCreateGroupDialogOpen] = React.useState(false);
    const [addDeviceModalOpen, setAddDeviceModalOpen] = React.useState(false);
    const trackClick = useAnalyticsClick();
    const theme = useTheme();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common"]);
    const { data: workspace } = useCurrentWorkspace();
    const isExpired = useCurrentSubscriptionExpired();
    const location = useLocation();
    const navigate = useNavigate();

    const { groupAbility } = PermissionService();
    const { features } = getSettings();

    React.useEffect(() => {
        const searchParams = queryString.parse(location.search);
        if (searchParams.openEnrollmentModal === "true") {
            setAddDeviceModalOpen(true);
            const newSearch = queryString.stringify(
                { ...searchParams, openEnrollmentModal: undefined },
                { skipEmptyString: true, skipNull: true },
            );
            navigate({ search: newSearch }, { replace: true });
        }
    }, [location.search, navigate]);

    const selectBatchActionMode: SelectBatchActionMode = (entity, type) => {
        dispatch(setBatchMode({ type, entity }));
    };

    const getTooltipTitle = () => {
        if (isExpired) {
            return t("pages:devicePage.tooltip.subscriptionExpired");
        }
        if (groupAbility(null, workspace).cannot("create", "Group")) {
            return t("pages:devicePage.tooltip.yourRoleDoesNotAllowYouToCreateGroups");
        }
        return "";
    };

    const handleAddDevice = () => {
        if (features.enrollmentFlowEnabled) {
            navigate("/dashboard/enrollment");
            return;
        }
        setAddDeviceModalOpen(true);
    };

    return (
        <React.Fragment>
            <DropdownContainer
                label={t("common:button.manage")}
                icon={<i className="fa-solid fa-gear" style={{ fontSize: theme.spacing(4) }} />}
                anchorRef={anchorRef}
            >
                <DropdownItem
                    onClick={handleAddDevice}
                    label={t("pages:devicePage.button.addDevice")}
                    iconName="fa-solid fa-plus"
                />
                <DropdownItem
                    label={t("pages:devicePage.button.addGroup")}
                    iconName="fa-solid fa-folder-tree"
                    onClick={trackClick(() => setCreateGroupDialogOpen(true), "add-group", "dropdown")}
                    disabled={groupAbility(null, workspace).cannot("create", "Group")}
                    tooltipProps={{
                        title: getTooltipTitle(),
                        hide: groupAbility(null, workspace).can("create", "Group"),
                    }}
                />

                <DropdownItem
                    label={t("pages:devicePage.button.moveLicenses")}
                    iconName="fa-solid fa-right-from-bracket"
                    onClick={trackClick(() => selectBatchActionMode("license", "move"), "move-licenses", "dropdown")}
                />
                <ExportCSV />

                <DropdownItem
                    onClick={trackClick(onSelectColumns, "select-columns", "dropdown")}
                    label={t("pages:devicePage.button.selectColumns")}
                    iconName="fa-solid fa-tasks"
                />
            </DropdownContainer>

            <AddDeviceModal open={addDeviceModalOpen} onClose={() => setAddDeviceModalOpen(false)} />
            <CreateGroupDialog open={isCreateGroupDialogOpen} onClose={() => setCreateGroupDialogOpen(false)} />
        </React.Fragment>
    );
};
